import { useEffect, useState } from "react";
import Header from "../../../components/Header";
import API from "../../../api";
import {
  Box,
  Button,
  Stack,
  Chip,
  Tooltip,
} from "@mui/material";
import SecurityDataGrid from "../../../components/dataGridSecurity";
import { useLocation } from "react-router-dom";
import { DataGridWithGraph } from "../../../components/dataGridWithBarGraphOnRight";
import IntelligenceTimeFilter from "../../../components/IntelligenceTimeFilter";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

dayjs.extend(utc);
dayjs.extend(timezone);

// Costruisce un bracket:
// ipSource === ip OR ipDestination === ip
const ipBracket = (ip, logic) => ({
  ...(logic ? { logic } : {}),
  filters: [
    {
      field: "ipSource",
      operator: "equals",
      value: ip,
    },
    {
      logic: "or",
      field: "ipDestination",
      operator: "equals",
      value: ip,
    },
  ],
});

const ThreatIntelligence = () => {
  const [seriesData, setSeriesData] = useState();
  const [selectedRow, setSelectedRow] = useState(null);
  const [categories, setCategories] = useState();
  const [threatIntelligence, setThreatIntelligence] = useState([]);
  const [chargedGraph, setChargedGraph] = useState(false);
  const [charged, setCharged] = useState(false);
  const [viewThreats, setViewThreats] = useState(false);
  const [threatsKey, setThreatsKey] = useState(0);
  const [passedFilter, setPassedFilter] = useState([]);

  const location = useLocation();

  const tipoLecs = sessionStorage.getItem("tipoLecs");

  const isSfp = [
    "LECS-CORE-SFP",
    "LECS-CORE-4-PRO",
    "LECS-CORE-SFP-PLUS",
  ].includes(tipoLecs);

  const tz =
    sessionStorage.getItem("timezone") || "Europe/Amsterdam";

  const getDefaultDateFilter = () => {
    const now = dayjs().tz(tz);

    return isSfp
      ? {
          dateFrom: now.subtract(1, "hour"),
          dateTo: now,
        }
      : {
          dateFrom: now.subtract(7, "day"),
          dateTo: now,
        };
  };

  const getInitialDateFilter = () => {
    const queryParams = new URLSearchParams(location.search);

    const dateFromParam = queryParams.get("dateFrom");
    const dateToParam = queryParams.get("dateTo");

    if (!dateFromParam || !dateToParam) {
      return getDefaultDateFilter();
    }

    const parsedDateFrom = dayjs(dateFromParam).tz(tz);
    const parsedDateTo = dayjs(dateToParam).tz(tz);

    if (
      !parsedDateFrom.isValid() ||
      !parsedDateTo.isValid() ||
      parsedDateFrom.isAfter(parsedDateTo)
    ) {
      return getDefaultDateFilter();
    }

    return {
      dateFrom: parsedDateFrom,
      dateTo: parsedDateTo,
    };
  };

  const [payload, setPayload] = useState({
    dateFrom: "",
    dateTo: "",
    detail: "",
    exact_detail: "",
    ipSource: "",
    ipDestination: "",
    source: "",
    destination: "",
    ipMap: false,
    tipo: 4,
  });

  const [dateFilter, setDateFilter] = useState(
    getInitialDateFilter
  );

  /*
   * Legge i parametri dalla query string.
   *
   * Se sono presenti dateFrom e dateTo valide:
   * - aggiorna il payload;
   * - aggiorna IntelligenceTimeFilter.
   *
   * Se non sono presenti:
   * - mantiene il comportamento predefinito;
   * - 1 ora per dispositivi SFP;
   * - 7 giorni per gli altri dispositivi.
   */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    const exactDetail = queryParams.get("exact_details");
    const detail = queryParams.get("details");

    const isPrivateSource = queryParams.get(
      "isPrivateSource"
    );

    const isPrivateDestination = queryParams.get(
      "isPrivateDestination"
    );

    const ipMap = queryParams.get("ipMap");

    const ipSource =
      queryParams.get("ipSource") || ipMap;

    const ipDestination =
      queryParams.get("ipDestination") || ipMap;

    let dateFrom;
    let dateTo;

    const clickedData = queryParams.get("clickedData");
    const advtimeStamp = queryParams.get("advtimeStamp");
    const dateFromParam = queryParams.get("dateFrom");
    const dateToParam = queryParams.get("dateTo");

    if (clickedData) {
      if (isSfp) {
        dateFrom = dayjs
          .tz(clickedData, tz)
          .startOf("hour");

        dateTo = dateFrom
          .endOf("hour")
          .millisecond(0);
      } else {
        dateFrom = dayjs
          .tz(clickedData, tz)
          .startOf("day");

        dateTo = dateFrom
          .endOf("day")
          .millisecond(0);
      }
    } else if (advtimeStamp) {
      const advDate = dayjs(advtimeStamp).tz(tz);

      if (advDate.isValid()) {
        dateFrom = advDate.startOf("day");
        dateTo = advDate.endOf("day");
      }
    } else if (dateFromParam && dateToParam) {
      const parsedDateFrom = dayjs(dateFromParam).tz(tz);
      const parsedDateTo = dayjs(dateToParam).tz(tz);

      if (
        parsedDateFrom.isValid() &&
        parsedDateTo.isValid() &&
        !parsedDateFrom.isAfter(parsedDateTo)
      ) {
        dateFrom = parsedDateFrom;
        dateTo = parsedDateTo;
      }
    }

    /*
     * Se non sono state trovate date valide nella query string,
     * vengono utilizzati i valori predefiniti.
     */
    if (!dateFrom || !dateTo) {
      const defaultDateFilter = getDefaultDateFilter();

      dateFrom = defaultDateFilter.dateFrom;
      dateTo = defaultDateFilter.dateTo;
    }

    /*
     * Aggiorna anche dateFilter.
     * In questo modo IntelligenceTimeFilter riceve le date
     * provenienti dalla query string.
     */
    setDateFilter({
      dateFrom,
      dateTo,
    });

    setPayload((previousPayload) => ({
      ...previousPayload,
      dateFrom,
      dateTo,
      detail: detail || "",
      exact_detail: exactDetail || "",
      isPrivateSource,
      isPrivateDestination,
      ipMap: Boolean(ipMap),
      source: ipSource || "",
      destination: ipDestination || "",
    }));

    if (queryParams.get("ipMap") || queryParams.get("clickedData")) setViewThreats(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queryParams is a new URLSearchParams object every render; adding it (plus isSfp/tz) would refire this mount-only effect and re-trigger setPayload on every render
  }, []);

  useEffect(() => {
    if (!dateFilter.dateFrom || !dateFilter.dateTo) {
      return undefined;
    }

    setChargedGraph(false);

    const controller = new AbortController();

    const params = {
      scope: "internal",
      dateFrom: dateFilter.dateFrom
        .utc()
        .format("YYYY-MM-DD HH:mm"),
      dateTo: dateFilter.dateTo
        .utc()
        .format("YYYY-MM-DD HH:mm"),
    };

    if (selectedRow) {
      params.ip = selectedRow;
      params.scope = "external";
    }

    API.getThreatIntelligence(
      params,
      controller.signal
    )
      .then((response) => {
        if (selectedRow) {
          const chartData = response.data.map((item) => ({
            count: item.count,
            ip: item.ip,
          }));

          setCategories(
            chartData.map((item) => item.ip)
          );

          setSeriesData(chartData);
          setChargedGraph(true);
        } else {
          setCharged(false);

          setThreatIntelligence(
            response.data.map((item, index) => ({
              id: item.ip ?? index,
              ...item,
            }))
          );

          setCharged(true);
        }
      })
      .catch((error) => {
        if (
          error.name !== "CanceledError" &&
          error.name !== "AbortError" &&
          error.message !== "canceled"
        ) {
          console.error(error);
        }

        setCharged(true);
        setChargedGraph(true);
      });

    return () => {
      controller.abort();
    };
  }, [dateFilter, selectedRow]);

  /*
   * Naviga a Threats Analysis.
   *
   * IP singolo:
   * (src === IP OR dst === IP)
   *
   * Due IP distinti:
   * (src === IP1 OR dst === IP1)
   * AND
   * (src === IP2 OR dst === IP2)
   *
   * Nessun IP:
   * nessun filtro
   */
  const goToThreats = (filter = {}) => {
    const ip1 = filter?.ipSource || "";
    const ip2 = filter?.ipDestination || "";

    let builtFilter = [];

    if (ip1 && ip2 && ip1 !== ip2) {
      builtFilter = [
        ipBracket(ip1),
        ipBracket(ip2, "and"),
      ];
    } else if (ip1 || ip2) {
      builtFilter = [
        ipBracket(ip1 || ip2),
      ];
    }

    setPassedFilter(builtFilter);

    setPayload((previousPayload) => ({
      ...previousPayload,
      ipMap: false,
      source: "",
      destination: "",
      ipSource: "",
      ipDestination: "",
    }));

    setThreatsKey((previousKey) => previousKey + 1);
    setViewThreats(true);
    setSelectedRow(null);
  };

  const handleDateChange = (newDateFilter) => {
    const newDateFrom = dayjs(
      newDateFilter.dateFrom
    ).tz(tz);

    const newDateTo = dayjs(
      newDateFilter.dateTo
    ).tz(tz);

    if (
      !newDateFrom.isValid() ||
      !newDateTo.isValid() ||
      newDateFrom.isAfter(newDateTo)
    ) {
      return;
    }

    setDateFilter({
      dateFrom: newDateFrom,
      dateTo: newDateTo,
    });

    /*
     * Aggiorna anche il payload usato da SecurityDataGrid,
     * mantenendo sincronizzate le date tra le due viste.
     */
    setPayload((previousPayload) => ({
      ...previousPayload,
      dateFrom: newDateFrom,
      dateTo: newDateTo,
    }));
  };

  return (
    <Box m="20px">
      <Header
        title="Threat Intelligence"
        subtitle=""
        desc="LOGs for each IP are grouped here, offering instant overviews on those generating the most LOGs."
      />

      <Box
        sx={{
          width: "97%",
          minHeight: "100vh",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{ mt: 3 }}
        >
          <Stack direction="row" gap={2}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: viewThreats
                  ? "#2d2d2d"
                  : "whitesmoke !important",
                color: viewThreats
                  ? "whitesmoke"
                  : "#131C2D",
                "&:hover": {
                  backgroundColor:
                    "#eaeaea !important",
                  color: "#131C2D",
                },
              }}
              style={{ fontWeight: "bold" }}
              onClick={() => setViewThreats(false)}
            >
              Intelligence Statics
            </Button>

            <Button
              variant="contained"
              sx={{
                backgroundColor: viewThreats
                  ? "whitesmoke !important"
                  : "#2d2d2d",
                color: viewThreats
                  ? "#131C2D"
                  : "whitesmoke",
                "&:hover": {
                  backgroundColor:
                    "#eaeaea !important",
                  color: "#131C2D",
                },
              }}
              style={{ fontWeight: "bold" }}
              onClick={() => goToThreats()}
            >
              Threats Analysis
            </Button>
          </Stack>

          {!viewThreats && selectedRow && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <Chip
                label={selectedRow}
                size="small"
                onDelete={() => setSelectedRow(null)}
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 700,
                  backgroundColor: "#459095",
                  color: "#fff",
                }}
              />

              <Tooltip
                title={`Threats dove ${selectedRow} è src oppure dst`}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    <OpenInNewIcon
                      sx={{ fontSize: 14 }}
                    />
                  }
                  onClick={() =>
                    goToThreats({
                      ipSource: selectedRow,
                      ipDestination: selectedRow,
                    })
                  }
                  sx={{
                    fontSize: "0.75rem",
                    height: 28,
                  }}
                >
                  View Threats
                </Button>
              </Tooltip>
            </Stack>
          )}
        </Stack>

        {!viewThreats && (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            sx={{ mt: 3 }}
          >
            <IntelligenceTimeFilter
              key={`${dateFilter.dateFrom?.valueOf()}-${dateFilter.dateTo?.valueOf()}`}
              title="Time Filter"
              onDateChange={handleDateChange}
              initialDateFrom={dateFilter.dateFrom}
              initialDateTo={dateFilter.dateTo}
            />
          </Stack>
        )}

        <Box
          sx={{
            width: "100%",
            maxWidth: {
              xs: "95vw",
              sm: "95vw",
              md: "100%",
            },
            overflowX: {
              xs: "auto",
              sm: "auto",
              md: "unset",
            },
            mt: 3,
          }}
        >
          {viewThreats ? (
            <SecurityDataGrid
              filterKey={threatsKey}
              tipo={4}
              tableKey="threat"
              passedPayload={payload}
              passedFilter={passedFilter}
            />
          ) : (
            <DataGridWithGraph
              chargedGraph={chargedGraph}
              tableData={threatIntelligence}
              charged={charged}
              seriesData={seriesData}
              selectedRow={selectedRow}
              categories={categories}
              setSelectedRow={setSelectedRow}
              heightTable="70vh"
              onBarClick={goToThreats}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ThreatIntelligence;